import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from './ui';

interface APICredential {
  id: string;
  api_username: string;
  member_id: string;
  member_name?: string;
  organization_name: string;
  rate_limit_per_hour: number;
  allowed_ips: string[];
  status: string;
  created_at: string;
  last_used_at: string | null;
}

export default function APICredentialsManagement() {
  const { confirm } = useConfirm();
  const [credentials, setCredentials] = useState<APICredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<{ username: string; key: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    api_username: '',
    member_id: '',
    rate_limit_per_hour: 1000,
    allowed_ips: ''
  });

  const [members, setMembers] = useState<{ id: string; organization_name: string }[]>([]);

  useEffect(() => {
    fetchCredentials();
    fetchMembers();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/api-credentials');
      const data = await response.json();

      if (data.success) {
        setCredentials(data.credentials);
      } else {
        setError(data.message || 'Kunne ikke laste API-legitimasjon');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await authenticatedFetch('/api/admin/members');
      const data = await response.json();
      if (data.success) {
        setMembers(data.members);
      }
    } catch (err) {
      console.error('Kunne ikke laste medlemmer:', err);
    }
  };

  const generateAPIKey = () => {
    // Generate a random 32-character API key
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleCreateCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const apiKey = generateAPIKey();
      const allowedIps = formData.allowed_ips
        .split(',')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);

      const response = await authenticatedFetch('/api/admin/api-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_username: formData.api_username,
          api_key: apiKey,
          member_id: formData.member_id,
          rate_limit_per_hour: formData.rate_limit_per_hour,
          allowed_ips: allowedIps
        })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedKey({
          username: formData.api_username,
          key: apiKey
        });
        setSuccess('API-legitimasjon opprettet');
        setShowNewForm(false);
        setFormData({
          api_username: '',
          member_id: '',
          rate_limit_per_hour: 1000,
          allowed_ips: ''
        });
        fetchCredentials();
      } else {
        setError(data.message || 'Kunne ikke opprette API-legitimasjon');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteCredential = async (id: string) => {
    const confirmed = await confirm({
      title: 'Slett API-legitimasjon',
      message: 'Er du sikker på at du vil slette denne API-legitimasjonen?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      setError('');
      const response = await authenticatedFetch(`/api/admin/api-credentials/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('API-legitimasjon slettet');
        fetchCredentials();
      } else {
        setError(data.message || 'Kunne ikke slette API-legitimasjon');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      setError('');
      const response = await authenticatedFetch(`/api/admin/api-credentials/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`API-legitimasjon ${newStatus === 'active' ? 'aktivert' : 'deaktivert'}`);
        fetchCredentials();
      } else {
        setError(data.message || 'Kunne ikke oppdatere API-legitimasjon');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Kopiert til utklippstavle');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">API-legitimasjon</h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer API-legitimasjon for eksterne LMS-systemer
          </p>
        </div>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700"
        >
          <Plus className="h-5 w-5" />
          Ny legitimasjon
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {generatedKey && (
        <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900">Viktig: Lagre API-nøkkelen din</h3>
              <p className="text-sm text-yellow-800 mt-1">
                Dette er eneste gang du vil se denne API-nøkkelen. Kopier den nå og lagre den sikkert.
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded border border-yellow-200 space-y-2">
            <div>
              <label className="text-xs font-semibold text-gray-600">API Brukernavn:</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 bg-gray-50 px-3 py-2 rounded font-mono text-sm">
                  {generatedKey.username}
                </code>
                <button
                  onClick={() => copyToClipboard(generatedKey.username)}
                  className="p-2 hover:bg-gray-100 rounded"
                  title="Kopier"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">API-nøkkel:</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 bg-gray-50 px-3 py-2 rounded font-mono text-sm break-all">
                  {generatedKey.key}
                </code>
                <button
                  onClick={() => copyToClipboard(generatedKey.key)}
                  className="p-2 hover:bg-gray-100 rounded"
                  title="Kopier"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={() => setGeneratedKey(null)}
            className="mt-3 text-sm text-yellow-800 underline hover:text-yellow-900"
          >
            Jeg har lagret API-nøkkelen
          </button>
        </div>
      )}

      {showNewForm && (
        <form onSubmit={handleCreateCredential} className="bg-white border rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold">Opprett ny API-legitimasjon</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Brukernavn *
            </label>
            <input
              type="text"
              required
              value={formData.api_username}
              onChange={(e) => setFormData({ ...formData, api_username: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              placeholder="f.eks. its_learning_api"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medlemsorganisasjon *
            </label>
            <select
              required
              value={formData.member_id}
              onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
            >
              <option value="">Velg organisasjon...</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>
                  {member.organization_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grense (forespørsler/time)
            </label>
            <input
              type="number"
              required
              min="1"
              value={formData.rate_limit_per_hour}
              onChange={(e) => setFormData({ ...formData, rate_limit_per_hour: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tillatte IP-adresser (valgfritt, kommaseparert)
            </label>
            <input
              type="text"
              value={formData.allowed_ips}
              onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              placeholder="f.eks. 192.168.1.1, 10.0.0.1"
            />
            <p className="text-xs text-gray-500 mt-1">
              La stå tom for å tillate forespørsler fra alle IP-adresser
            </p>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700"
            >
              Opprett legitimasjon
            </button>
            <button
              type="button"
              onClick={() => setShowNewForm(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Avbryt
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Brukernavn
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organisasjon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Grense
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sist brukt
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {credentials.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  Ingen API-legitimasjon funnet. Opprett en for å komme i gang.
                </td>
              </tr>
            ) : (
              credentials.map((cred) => (
                <tr key={cred.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-gray-400" />
                      <span className="font-mono text-sm">{cred.api_username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {cred.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {cred.rate_limit_per_hour}/hr
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        cred.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {cred.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cred.last_used_at
                      ? new Date(cred.last_used_at).toLocaleString('nb-NO')
                      : 'Aldri'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleStatus(cred.id, cred.status)}
                        className="text-kf-blue-600 hover:text-kf-blue-900"
                        title={cred.status === 'active' ? 'Deaktiver' : 'Aktiver'}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCredential(cred.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Slett"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
